import * as React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, Globe, Plus, Search } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { DataTable } from "@/components/data-table";
import { EmptyState } from "@/components/ui/empty";
import { DomainStatusPill } from "@/components/ui/status-pill";
import { listDomainsPaged } from "@/api/domains";
import { Pagination } from "@/components/pagination";
import { formatRelativeTime } from "@/lib/utils";
import type { DomainDto, DomainStatus } from "@/types/api";
import { AdvertiserSelect } from "@/components/advertiser-select";
import { AdvertiserCell, useAdvertiserLookup } from "@/components/advertiser-cell";
import { ownerFilterFromUrl, ownerFilterParam, ownerFilterToUrl } from "@/api/commerce";

/**
 * Friendly status filter grouping. The platform's actual state machine has
 * 8 internal states; ops typically only care about 5 buckets. We expand
 * each bucket to the underlying states server-side. Picking "Provisioning"
 * here issues N queries (one per state) and merges — fine for the small
 * tail of in-flight rows.
 */
type FriendlyFilter = "all" | "live" | "available" | "provisioning" | "retired" | "failed";
const FRIENDLY_OPTIONS: Array<{ value: FriendlyFilter; label: string }> = [
    { value: "all", label: "All" },
    { value: "live", label: "Live (Assigned)" },
    { value: "available", label: "Available (idle)" },
    { value: "provisioning", label: "Provisioning…" },
    { value: "retired", label: "Retired" },
    { value: "failed", label: "Failed" },
];
const PROVISIONING_STATES: DomainStatus[] = ["PURCHASING", "NS_DELEGATED", "ZONE_ACTIVE", "WORKER_ATTACHED"];

function statusForFilter(f: FriendlyFilter): DomainStatus | undefined {
    switch (f) {
        case "all": return undefined;
        case "live": return "ASSIGNED";
        case "available": return "AVAILABLE";
        case "retired": return "RETIRED";
        case "failed": return "FAILED";
        case "provisioning": return undefined; // handled separately
    }
}

export function DomainsListPage(): React.ReactNode {
    // Read initial filter from URL `?status=` so the Overview "X failed" alert
    // can deep-link into the right view. We also drive the advertiser filter
    // off the same searchParams so reload + share-link survives.
    const [searchParams, setSearchParams] = useSearchParams();
    const initialFilter: FriendlyFilter = (() => {
        const s = searchParams.get("status");
        if (s === "FAILED") return "failed";
        if (s === "AVAILABLE") return "available";
        if (s === "ASSIGNED") return "live";
        if (s === "RETIRED") return "retired";
        return "all";
    })();
    const [filter, setFilter] = React.useState<FriendlyFilter>(initialFilter);
    const [searchInput, setSearchInput] = React.useState("");
    const [search, setSearch] = React.useState("");
    React.useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 200);
        return () => clearTimeout(t);
    }, [searchInput]);

    const PAGE_SIZE = 20;
    const [pageIndex, setPageIndex] = React.useState(0);
    React.useEffect(() => setPageIndex(0), [filter, search]);

    // Advertiser filter: undefined = "All", null = "Unassigned" (-1 wire).
    const advertiserId = ownerFilterFromUrl(searchParams.get("advertiserId"));
    const setAdvertiserId = React.useCallback(
        (next: number | null | undefined): void => {
            setSearchParams(
                (p) => {
                    const v = ownerFilterToUrl(next);
                    if (v == null) p.delete("advertiserId");
                    else p.set("advertiserId", v);
                    return p;
                },
                { replace: true },
            );
            setPageIndex(0);
        },
        [setSearchParams],
    );
    const advLookup = useAdvertiserLookup();
    const showAdvertiserColumn = advLookup.advertisers.length > 0;

    // Provisioning filter expands to all 4 internal states — fetch each and concat.
    // The total counts in this bucket are tiny in practice (single-digit).
    const provisioningQueries = useQuery({
        queryKey: ["domains", "provisioning-bundle", pageIndex, advertiserId],
        queryFn: async () => {
            const pages = await Promise.all(PROVISIONING_STATES.map((st) =>
                listDomainsPaged({
                    status: st,
                    page: 0,
                    size: 100,
                    sort: "createdAt,desc",
                    advertiserId: ownerFilterParam(advertiserId),
                })
            ));
            const rows = pages.flatMap((p) => p.content);
            return { rows, total: rows.length };
        },
        enabled: filter === "provisioning",
        placeholderData: (prev) => prev,
    });

    const status = statusForFilter(filter);
    const domainsQuery = useQuery({
        queryKey: ["domains", filter, pageIndex, advertiserId],
        queryFn: () =>
            listDomainsPaged({
                status,
                page: pageIndex,
                size: PAGE_SIZE,
                sort: "createdAt,desc",
                advertiserId: ownerFilterParam(advertiserId),
            }),
        enabled: filter !== "provisioning",
        placeholderData: (prev) => prev,
    });

    // Failed-count banner — independent of the current filter so it surfaces
    // problems even when you're looking at "Live".
    const failedCountQuery = useQuery({
        queryKey: ["domains", "failed-count"],
        queryFn: () => listDomainsPaged({ status: "FAILED", page: 0, size: 1 }),
    });
    const failedCount = failedCountQuery.data?.totalElements ?? 0;

    const rawRows: DomainDto[] = filter === "provisioning"
        ? (provisioningQueries.data?.rows ?? [])
        : (domainsQuery.data?.content ?? []);

    // Client-side hostname filter — cheap and operator-friendly. Server-side
    // search would be nicer for huge inventories; revisit when total > a few
    // hundred and the in-page filter feels limiting.
    const rows = search
        ? rawRows.filter((d) => d.hostname.toLowerCase().includes(search))
        : rawRows;

    const isLoading = filter === "provisioning" ? provisioningQueries.isLoading : domainsQuery.isLoading;
    const isFetching = filter === "provisioning" ? provisioningQueries.isFetching : domainsQuery.isFetching;

    return (
        <>
            <PageHeader
                title="Domains"
                description="Platform-owned domains and their provisioning + assignment state."
                actions={
                    <div className="flex items-center gap-2">
                        <Select value={filter} onValueChange={(v) => setFilter(v as FriendlyFilter)}>
                            <SelectTrigger className="w-48" data-testid="domain-status-filter" aria-label="Filter by status">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {FRIENDLY_OPTIONS.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button asChild data-testid="buy-domain-button">
                            <Link to="/domains/purchase">
                                <Plus className="h-4 w-4" />
                                Buy domain
                            </Link>
                        </Button>
                    </div>
                }
            />
            <div className="p-6 space-y-3">
                {/* Failed-count alert — surfaces regardless of filter. */}
                {failedCount > 0 && filter !== "failed" && (
                    <Link
                        to="/domains?status=FAILED"
                        onClick={() => setFilter("failed")}
                        className="flex items-center gap-3 rounded-md border border-destructive/40 bg-destructive/5 px-4 py-2 text-sm text-destructive hover:bg-destructive/10"
                        data-testid="domains-failed-banner"
                    >
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        <span><strong>{failedCount}</strong> domain{failedCount === 1 ? "" : "s"} failed provisioning — needs attention.</span>
                        <ArrowRight className="ml-auto h-4 w-4" />
                    </Link>
                )}

                {/* Toolbar: search + advertiser filter + subtle "fetching" indicator. */}
                <div className="flex items-center gap-2">
                    <div className="flex flex-1 items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 min-w-[260px]">
                        <Search className="h-4 w-4 text-muted-foreground" />
                        <Input
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search by hostname…"
                            className="h-7 border-0 bg-transparent px-0 focus-visible:ring-0"
                            data-testid="domains-search"
                        />
                    </div>
                    {showAdvertiserColumn && (
                        <div className="w-[200px]">
                            <AdvertiserSelect
                                value={advertiserId}
                                onChange={setAdvertiserId}
                                data-testid="domains-advertiser-filter"
                            />
                        </div>
                    )}
                    {isFetching && !isLoading && (
                        <Spinner size="sm" />
                    )}
                </div>

                {isLoading ? (
                    <div className="flex items-center justify-center py-16">
                        <Spinner size="lg" />
                    </div>
                ) : (
                    <DataTable<DomainDto>
                        dataTestid="domains-table"
                        columns={[
                            {
                                key: "hostname",
                                header: "Hostname",
                                cell: (d) => (
                                    <Link to={`/domains/${d.id}`} className="font-medium hover:underline" data-testid={`domain-link-${d.id}`}>
                                        {d.hostname}
                                    </Link>
                                ),
                            },
                            { key: "status", header: "Status", cell: (d) => <DomainStatusPill status={d.status} /> },
                            {
                                key: "active",
                                header: "Active page",
                                cell: (d) =>
                                    d.activePageVersionId ? (
                                        d.activePageName && d.activePageId ? (
                                            <Link
                                                to={`/pages/${d.activePageId}`}
                                                onClick={(e) => e.stopPropagation()}
                                                className="text-sm hover:underline"
                                            >
                                                <span className="font-medium">{d.activePageName}</span>
                                                {d.activePageVersionNum != null && (
                                                    <span className="ml-1.5 text-xs text-muted-foreground">v{d.activePageVersionNum}</span>
                                                )}
                                            </Link>
                                        ) : (
                                            <code className="font-mono text-xs text-muted-foreground">v#{d.activePageVersionId}</code>
                                        )
                                    ) : (
                                        <span className="text-muted-foreground">—</span>
                                    ),
                            },
                            {
                                key: "ga",
                                header: "GA code",
                                cell: (d) => <span className="text-muted-foreground">{d.gaCode || "—"}</span>,
                            },
                            ...(showAdvertiserColumn ? [{
                                key: "advertiser",
                                header: "Advertiser",
                                cell: (d: DomainDto) => (
                                    <AdvertiserCell advertiserId={d.advertiserId} lookup={advLookup.byId} />
                                ),
                            }] : []),
                            {
                                key: "updated",
                                header: "Updated",
                                cell: (d) => <span className="text-muted-foreground">{formatRelativeTime(d.updatedAt)}</span>,
                            },
                        ]}
                        rows={rows}
                        rowKey={(d) => d.id}
                        // No onRowClick — the hostname column is already a link
                        // (avoids the double-click affordance the previous version had).
                        emptyState={
                            <EmptyState
                                icon={<Globe className="h-8 w-8" />}
                                title={search ? `No hostnames match "${searchInput}"` : "No domains"}
                                description={
                                    search
                                        ? "Try a different search term."
                                        : filter === "all"
                                            ? "Purchase your first domain to get started."
                                            : `No domains in this state.`
                                }
                                action={
                                    !search && filter === "all" ? (
                                        <Button asChild>
                                            <Link to="/domains/purchase">
                                                <Plus className="h-4 w-4" />
                                                Buy domain
                                            </Link>
                                        </Button>
                                    ) : null
                                }
                            />
                        }
                    />
                )}
                {filter !== "provisioning" && domainsQuery.data && (
                    <Pagination
                        page={domainsQuery.data}
                        pageSize={PAGE_SIZE}
                        currentPage={pageIndex}
                        onPage={setPageIndex}
                    />
                )}
            </div>
        </>
    );
}
